/**
 * HoProjectCard - Exact recreation from c34a46ad-86fa-438c-bdc0-955a58a592f9.javascript
 */

import * as React from "react";
import type { HoProject } from "../../data/mock-homeowner";

import { Avatar, FloatPanel, Icn, Pill, Progress } from "./design-primitives";

interface HoProjectCardProps {
	project: HoProject;
	accent: string;
	onOpen: () => void;
	makingPrimary?: boolean;
	onMakePrimary?: () => void;
}

function formatLakh(amount: number): string {
	return `₹${(amount / 100000).toFixed(1)}L`;
}

function describeProposals(count: number): string {
	if (count === 0) return "No proposals waiting";
	return count === 1 ? "1 proposal received" : `${count} proposals received`;
}

export function HoProjectCard({
	project: p,
	accent,
	onOpen,
	makingPrimary = false,
	onMakePrimary,
}: HoProjectCardProps) {
	const [hover, setHover] = React.useState(false);

	return (
		<FloatPanel
			elev={hover ? 3 : 2}
			padding={0}
			style={{
				overflow: "hidden",
				cursor: "pointer",
				transform: hover ? "translateY(-3px)" : "none",
				transition:
					"transform 350ms cubic-bezier(0.22,1,0.36,1), box-shadow 350ms ease",
			}}
			onClick={onOpen}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			{/* Hero Image */}
			<div
				style={{
					position: "relative",
					aspectRatio: "16/10",
					overflow: "hidden",
				}}
			>
				<img
					src={p.img}
					alt=""
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						transform: hover ? "scale(1.04)" : "scale(1)",
						transition: "transform 700ms cubic-bezier(0.22,1,0.36,1)",
					}}
				/>
				<div
					style={{
						position: "absolute",
						inset: 0,
						background:
							"linear-gradient(180deg, color-mix(in srgb, var(--color-dashboard-scrim) 0%, transparent) 42%, color-mix(in srgb, var(--color-dashboard-scrim) 66%, transparent))",
					}}
				/>
				{p.isPrimary && (
					<Pill
						tone="active"
						style={{
							position: "absolute",
							top: 12,
							right: 12,
							background:
								"color-mix(in srgb, var(--color-dashboard-paper) 92%, transparent)",
						}}
					>
						Primary
					</Pill>
				)}
				<Pill
					tone={p.stageTone}
					style={{
						position: "absolute",
						top: 12,
						left: 12,
						background:
							"color-mix(in srgb, var(--color-dashboard-paper) 92%, transparent)",
					}}
				>
					{p.stage}
				</Pill>
				<div style={{ position: "absolute", left: 16, right: 16, bottom: 14 }}>
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontSize: 22,
							fontWeight: 500,
							color: "var(--color-dashboard-on-fill)",
							lineHeight: 1.1,
						}}
					>
						{p.name}
					</div>
					<div
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 8.5,
							letterSpacing: "0.12em",
							color:
								"color-mix(in srgb, var(--color-dashboard-on-fill) 85%, transparent)",
							marginTop: 5,
							textTransform: "uppercase",
						}}
					>
						{p.loc} · {p.config}
					</div>
				</div>
			</div>

			{/* Card Body */}
			<div style={{ padding: "16px 18px 18px" }}>
				{p.designer ? (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 9,
							marginBottom: 12,
						}}
					>
						<Avatar
							initials={p.designerIni ?? ""}
							tone={p.designerTone ?? "amber"}
							size={26}
						/>
						<span
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								color: "var(--color-dashboard-muted-dark)",
							}}
						>
							{p.designer} ·{" "}
							<span style={{ color: "var(--color-dashboard-muted)" }}>
								{p.studio}
							</span>
						</span>
					</div>
				) : p.proposalsCount !== undefined ? (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							marginBottom: 12,
							fontFamily: "var(--font-body)",
							fontSize: 12.5,
							color: accent,
							fontWeight: 600,
						}}
					>
						<Icn name="inbox" size={14} />
						{describeProposals(p.proposalsCount)}
					</div>
				) : null}

				{p.pct > 0 && p.pct < 100 && (
					<>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "baseline",
								marginBottom: 6,
							}}
						>
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9,
									letterSpacing: "0.1em",
									color: "var(--color-dashboard-muted)",
									textTransform: "uppercase",
								}}
							>
								Day {p.day} of {p.days}
							</span>
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 18,
									color: "var(--color-dashboard-text)",
								}}
							>
								{p.pct}%
							</span>
						</div>
						<Progress value={p.pct} fill={accent} />
					</>
				)}

				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginTop: 14,
						paddingTop: 14,
						borderTop: "1px solid var(--color-dashboard-border-soft)",
					}}
				>
					<span
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							fontFamily: "var(--font-body)",
							fontSize: 12,
							color: "var(--color-dashboard-muted-dark)",
						}}
					>
						<Icn name="wallet" size={13} color="var(--color-dashboard-muted)" />
						{p.pct === 100
							? `Delivered ${formatLakh(p.budget)}`
							: `Budget ${formatLakh(p.budget)}`}
					</span>
					<div style={{ display: "flex", gap: 10, alignItems: "center" }}>
						{!p.isPrimary && onMakePrimary && (
							<button
								type="button"
								disabled={makingPrimary}
								onClick={(e) => {
									e.stopPropagation();
									onMakePrimary();
								}}
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									fontWeight: 600,
									color: makingPrimary
										? "var(--color-dashboard-muted)"
										: "var(--color-dashboard-accent-dark)",
									background: "none",
									border: "none",
									cursor: makingPrimary ? "not-allowed" : "pointer",
									padding: 0,
									opacity: makingPrimary ? 0.6 : 1,
								}}
							>
								{makingPrimary ? "Setting..." : "Make Primary"}
							</button>
						)}
						<span
							style={{
								display: "flex",
								alignItems: "center",
								gap: 5,
								fontFamily: "var(--font-body)",
								fontSize: 12,
								fontWeight: 600,
								color: accent,
							}}
						>
							Open <Icn name="arrowRight" size={13} />
						</span>
					</div>
				</div>
			</div>
		</FloatPanel>
	);
}
